/**
 * Page d'arrivée du lien « Code oublié ? » : /pin-reset?token=…
 *
 * Publique (hors `RequireAuth`) : le lien s'ouvre souvent dans un navigateur
 * où le parent n'est pas connecté, et le jeton reçu par e-mail suffit à
 * prouver qu'il a la main sur le compte. L'appel part donc sans
 * authentification (voir `resetPin` dans api.ts).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container, PageLayout, Card, Button, Headline, MutedText } from "../components/ui";
import { Logo } from "../components/Logo";
import { resetPin } from "../lib/dataSource";
import { useAuth } from "../lib/auth";

export default function PinReset() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, refreshMe } = useAuth();
  const [state, setState] = useState<"loading" | "success" | "error">("loading");
  // Le jeton est à usage unique : en StrictMode (dev) l'effet tourne deux
  // fois, et le second appel afficherait une fausse erreur.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = params.get("token");
    if (!token) {
      setState("error");
      return;
    }
    resetPin(token)
      .then(async () => {
        setState("success");
        // Connecté sur cet appareil : `kid_pin_set` doit retomber à false,
        // sinon l'espace parent redemanderait un code qui n'existe plus.
        if (isAuthenticated) await refreshMe();
      })
      .catch(() => setState("error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageLayout>
      <Container className="flex min-h-screen flex-col justify-center gap-6 py-10">
        <div className="text-center">
          <Logo size={56} className="mb-3" />
          {state !== "loading" && (
            <Headline className="text-3xl">
              {state === "success" ? t("pinReset.successTitle") : t("pinReset.errorTitle")}
            </Headline>
          )}
        </div>
        <Card>
          <div className="flex flex-col gap-4">
            <MutedText>
              {state === "loading" ? t("pinReset.loading") : state === "success" ? t("pinReset.success") : t("pinReset.error")}
            </MutedText>
            {state !== "loading" && (
              <Button type="button" className="w-full" onClick={() => navigate("/", { replace: true })}>
                {t("pinReset.home")}
              </Button>
            )}
          </div>
        </Card>
      </Container>
    </PageLayout>
  );
}
